import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { useMantineColorScheme, LoadingOverlay, Center, Text, Paper, Stack, Container, Anchor, Button } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { ThemeProvider } from '@/themes/ThemeProvider';
import { notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Notifications } from '@mantine/notifications';
import { DatesProvider } from '@mantine/dates';
import { Suspense, lazy, useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/pt';
import 'dayjs/locale/ru';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AppShell } from '@/components/layout/AppShell';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useFavoritesSync } from '@/hooks/useFavoritesSync';
import { useMessagesSync } from '@/hooks/useMessagesSync';
import { useOrdersSync } from '@/hooks/useOrdersSync';
import { useMerchantReadySync } from '@/hooks/useMerchantReadySync';
import { useProfileSync } from '@/hooks/useProfileSync';
import { MultiCartProvider, useMultiCartContext } from '@/features/cart';
import { MultiCartDrawer } from '@teletienda/shared-ui-lib';
import { VerificationCodeModal } from '@/components/auth/VerificationCodeModal';
import { charonAPI } from '@/lib/api/client';
import { AuthProvider, useAuth, IdleSessionWarning, LoginPage as SharedLoginPage, RegisterForm, AuthGuard, getReturnUrl, AuthCallbackPage } from '@teletienda/shared-auth-ui';
import { NOTIFICATIONS_CONFIG, usePreferencesSync, useProfileRealtimeSync, useVersionNotification } from '@teletienda/shared-ui-lib';
import { WebSocketWrapper, CurrencyProvider, useCurrency } from '@/components/providers';
import { UserPreferencesProvider } from '@/providers/UserPreferencesProvider';
import { PlatformBootstrapProvider, useOAuthConfig } from '@/providers/PlatformBootstrapProvider';
import '@teletienda/shared-auth-ui/dist/style.css';
import { migrateAuthStorage } from './utils/authMigration';

// Run storage migration BEFORE AuthProvider initializes
// Migrates from 'auth-storage' to 'teletienda-auth' for SSO compatibility
migrateAuthStorage();

// Initialize dayjs plugins
dayjs.extend(customParseFormat);

// i18n is initialized in main.tsx before App renders

// Lazy loaded pages for better performance
const HomePage = lazy(() => import('@/pages/HomePage').then(module => ({ default: module.HomePage })));
const SearchPage = lazy(() => import('@/pages/SearchPage').then(module => ({ default: module.SearchPage })));
const MerchantsPage = lazy(() => import('@/pages/MerchantsPage').then(module => ({ default: module.MerchantsPage })));
const NewsRoadmapPage = lazy(() => import('@/pages/NewsRoadmapPage').then(module => ({ default: module.NewsRoadmapPage })));
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then(module => ({ default: module.ProfilePage })));
const OrdersPage = lazy(() => import('@/pages/OrdersPage').then(module => ({ default: module.OrdersPage })));
const FinancesPage = lazy(() => import('@/pages/FinancesPage/index.tsx').then(module => ({ default: module.FinancesPage })));
const FavoritesPage = lazy(() => import('@/pages/FavoritesPage').then(module => ({ default: module.FavoritesPage })));
const CartsPage = lazy(() => import('@/pages/CartsPage').then(module => ({ default: module.CartsPage })));
const MessagesPage = lazy(() => import('@/pages/MessagesPage').then(module => ({ default: module.MessagesPage })));
const CreateMerchantPage = lazy(() => import('@/pages/CreateMerchantPage').then(module => ({ default: module.CreateMerchantPage })));
const MyMerchantsPage = lazy(() => import('@/pages/MyMerchantsPage').then(module => ({ default: module.MyMerchantsPage })));
// LoginPage and RegisterPage are now wrappers using shared-auth-ui components (see below)
const EmailVerifiedPage = lazy(() => import('@/pages/AuthPage/EmailVerifiedPage').then(module => ({ default: module.EmailVerifiedPage })));
// Portal pages for unified cross-domain authentication (Story 8.2)
const PortalLoginPage = lazy(() => import('@/pages/PortalLoginPage').then(module => ({ default: module.PortalLoginPage })));
const PortalRegisterPage = lazy(() => import('@/pages/PortalRegisterPage').then(module => ({ default: module.PortalRegisterPage })));
const LoggedOutPage = lazy(() => import('@/pages/LoggedOutPage').then(module => ({ default: module.LoggedOutPage })));

// Admin pages
const DashboardPage = lazy(() => import('@/pages/admin/DashboardPage').then(module => ({ default: module.DashboardPage })));
const RolesPage = lazy(() => import('@/pages/admin/RolesPage').then(module => ({ default: module.RolesPage })));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage').then(module => ({ default: module.UsersPage })));
const AdminMerchantsPage = lazy(() => import('@/pages/admin/MerchantsPage').then(module => ({ default: module.MerchantsPage })));
const PermissionsPage = lazy(() => import('@/pages/admin/PermissionsPage').then(module => ({ default: module.PermissionsPage })));
const SettingsPage = lazy(() => import('@/pages/admin/SettingsPage').then(module => ({ default: module.SettingsPage })));
const NewsPage = lazy(() => import('@/pages/admin/NewsPage').then(module => ({ default: module.NewsPage })));
const CatalogAdminPage = lazy(() => import('@/pages/CatalogAdminPage').then(m => ({ default: m.CatalogAdminPage })));

// Test pages
const TestAPIPage = lazy(() => import('@/pages/TestAPIPage').then(module => ({ default: module.TestAPIPage })));

// Error pages
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage').then(module => ({ default: module.NotFoundPage })));
const UnauthorizedPage = lazy(() => import('@/pages/errors/UnauthorizedPage').then(module => ({ default: module.UnauthorizedPage })));
const ForbiddenPage = lazy(() => import('@/pages/errors/ForbiddenPage').then(module => ({ default: module.ForbiddenPage })));
const ServerErrorPage = lazy(() => import('@/pages/errors/ServerErrorPage').then(module => ({ default: module.ServerErrorPage })));
const ServiceUnavailablePage = lazy(() => import('@/pages/errors/ServiceUnavailablePage').then(module => ({ default: module.ServiceUnavailablePage })));

// Legal pages
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage').then(module => ({ default: module.PrivacyPage })));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage').then(module => ({ default: module.TermsPage })));
const DeleteMePage = lazy(() => import('@/pages/legal/DeleteMePage').then(module => ({ default: module.DeleteMePage })));

const queryClient = new QueryClient();

// API base URL: same origin, proxied by Vite (dev) or nginx (K8s).
// `/`, not `''`: an empty prefix leaves ky's request path page-relative, so a route more
// than one segment deep resolves it wrong — `/auth/callback/telegram` produced
// `POST /auth/callback/api/v1/...` → 405. Root-absolute makes route depth irrelevant.
const AUTH_API_URL = '/';

// Loading fallback component
function PageLoadingFallback() {
  const { t } = useTranslation('common');

  return (
    <Center h="100vh">
      <div>
        <LoadingOverlay visible />
        <Text size="lg" c="dimmed">{t('loading_page')}</Text>
      </div>
    </Center>
  );
}

// Login page wrapper using shared-auth-ui LoginPage
function LoginPage() {
  const navigate = useNavigate();
  const { facebookAppId } = useOAuthConfig();

  return (
    <SharedLoginPage
      logo={
        <Text
          size="xl"
          fw={700}
          c="brand"
          ta="center"
        >
          TeleTienda
        </Text>
      }
      onSuccess={() => navigate(getReturnUrl() || '/')}
      showSocialLogin
      facebookAppId={facebookAppId}
      // Where the guard was sending the user before it bounced them here. Without it the
      // Telegram redirect lands on `AuthCallbackPage`'s `defaultReturnUrl` — the home page.
      returnUrl={getReturnUrl() ?? undefined}
    />
  );
}

/**
 * OAuth redirect landing page — `/auth/callback/:provider`
 *
 * The provider is taken from the **path**, never guessed. `AuthCallbackPage` used to fall
 * back to `detectProvider()`, which answered `'google'` for `scope=openid …` — exactly
 * what Telegram sends back — so a shared path would have exchanged a Telegram code against
 * Google. Both segments are now registered byte-for-byte with their provider (Telegram in
 * BotFather, TGOIDC-1; Google in Cloud Console, TGOIDC-13), and the guess is gone.
 *
 * Mounted outside `AuthGuard`: the user lands here still logged out, and inside the guard
 * this would be a redirect loop.
 */
function OAuthCallbackPage() {
  const navigate = useNavigate();
  const { provider } = useParams<{ provider: string }>();

  // Only providers we actually serve on this route. An unknown segment is answered by
  // Facebook's branch — which refuses the redirect road outright — rather than trusted
  // from the URL or silently defaulted to whichever provider happens to be first.
  const knownProvider = provider === 'telegram' || provider === 'google' ? provider : 'facebook';

  return (
    <AuthCallbackPage
      provider={knownProvider}
      onSuccess={(returnUrl) => navigate(returnUrl, { replace: true })}
      onCancel={() => navigate('/login', { replace: true })}
      onError={(error) => {
        console.error('[OAuthCallback] authentication failed:', error);
      }}
    />
  );
}

// Register page wrapper using shared-auth-ui RegisterForm
function RegisterPage() {
  const { t } = useTranslation('common');
  const { loginWithCredentials } = useAuth();
  const navigate = useNavigate();

  // State for verification modal
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);

  // Ref to track credentials for cleanup (security)
  const credentialsRef = useRef(pendingCredentials);
  credentialsRef.current = pendingCredentials;

  // Security: Clear credentials on component unmount
  useEffect(() => {
    return () => {
      if (credentialsRef.current) {
        setPendingCredentials(null);
      }
    };
  }, []);

  // Capture email from form before shared RegisterForm submits
  const handleSubmitCapture = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const form = e.currentTarget.querySelector('form');
    if (form) {
      const emailInput = form.querySelector<HTMLInputElement>('[data-testid="register-email"]');
      const passwordInput = form.querySelector<HTMLInputElement>('[data-testid="register-password"]');
      if (emailInput && passwordInput) {
        setRegisteredEmail(emailInput.value);
        setPendingCredentials({
          email: emailInput.value,
          password: passwordInput.value,
        });
      }
    }
  }, []);

  // Auto-login and redirect after verification or skip
  const performAutoLogin = useCallback(async (immediateCredentials?: { email: string; password: string }) => {
    const credentials = immediateCredentials || pendingCredentials;
    if (!credentials) return;

    try {
      await loginWithCredentials(credentials);
      navigate('/');
    } catch {
      // If auto-login fails, redirect to login page
      notifications.show({
        title: t('auth_pages.register.error_title'),
        message: t('auth_pages.register.login_error'),
        color: 'yellow',
      });
      navigate('/login');
    } finally {
      // Security: Clear credentials after use
      setPendingCredentials(null);
    }
  }, [pendingCredentials, loginWithCredentials, navigate, t]);

  // Handler for successful registration from RegisterForm
  const handleSuccess = useCallback(() => {
    // Show verification modal (email and credentials already captured via handleSubmitCapture)
    setShowVerificationModal(true);
    notifications.show({
      title: t('auth_pages.register.welcome_title'),
      message: t('auth_pages.register.verification_code_sent'),
      color: 'green',
    });
  }, [t]);

  // Handler for successful verification
  const handleVerificationSuccess = useCallback(async (code: string) => {
    await charonAPI.post('/auth/verify-email', { email: registeredEmail, code }, false);
    setShowVerificationModal(false);
    await performAutoLogin();
  }, [registeredEmail, performAutoLogin]);

  // Handler for skip verification
  const handleSkipVerification = useCallback(async () => {
    setShowVerificationModal(false);
    await performAutoLogin();
  }, [performAutoLogin]);

  // Handler for resend code
  const handleResendCode = useCallback(async () => {
    await charonAPI.post('/auth/resend-verification', { email: registeredEmail }, false);
  }, [registeredEmail]);

  return (
    <>
      <Container size="sm" py="xl">
        <Center>
          <Stack gap="xl" w="100%" maw={400}>
            <Stack align="center" gap="md">
              <Text
                size="xl"
                fw={700}
                c="brand"
                ta="center"
              >
                TeleTienda
              </Text>
              <Text ta="center" c="dimmed">
                {t('auth_pages.register.create_account')}
              </Text>
            </Stack>

            <Paper shadow="md" p="xl" radius="md" withBorder>
              <div onSubmitCapture={handleSubmitCapture}>
                <RegisterForm
                  onSuccess={handleSuccess}
                  showLogin
                  onLoginClick={() => navigate('/login')}
                  termsUrl="/terms"
                  privacyUrl="/privacy"
                />
              </div>
            </Paper>

            <Center>
              <Anchor component={Link} to="/" size="sm">
                {t('common:back')}
              </Anchor>
            </Center>
          </Stack>
        </Center>
      </Container>

      {/* Verification Code Modal */}
      <VerificationCodeModal
        opened={showVerificationModal}
        email={registeredEmail}
        onClose={handleSkipVerification}
        onVerify={handleVerificationSuccess}
        onSkip={handleSkipVerification}
        onResend={handleResendCode}
      />
    </>
  );
}

/**
 * Multi-cart drawer wrapper component
 * Uses MultiCartContext to control drawer state
 */
function MultiCartDrawerWrapper() {
  const navigate = useNavigate();
  const {
    isDrawerOpen,
    closeDrawer,
    carts,
    totalItems,
    totalAmount,
    currency,
    isLoading,
    error,
  } = useMultiCartContext();
  const { formatDualCurrency } = useCurrency();

  const handleViewAll = () => {
    navigate('/carts');
  };

  // totalAmount from backend is the raw sum of cart subtotals in each merchant's
  // native currency (ARS), NOT converted to user's preferred currency.
  // Use the first cart's currency as the source currency for formatting.
  const sourceCurrency = carts.length > 0 ? carts[0].currency : currency;

  return (
    <MultiCartDrawer
      open={isDrawerOpen}
      onClose={closeDrawer}
      carts={carts}
      totalItems={totalItems}
      totalAmount={totalAmount}
      currency={sourceCurrency}
      isLoading={isLoading}
      error={error}
      formatPrice={(amount, cur) => formatDualCurrency(amount, cur)}
      onViewAll={handleViewAll}
    />
  );
}

/**
 * Announces route changes to screen readers via aria-live region.
 * Only renders when screenReader preference is enabled (data-screen-reader="true").
 */
function RouteAnnouncer() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    // Only announce if screen reader mode is enabled
    const isScreenReader = document.documentElement.getAttribute('data-screen-reader') === 'true';
    if (!isScreenReader) return;

    const timer = setTimeout(() => {
      setAnnouncement(document.title || location.pathname);
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {announcement}
    </div>
  );
}

function AppContent() {
  const { isAuthenticated, user } = useAuth();
  const { i18n, t } = useTranslation('common');
  const { setColorScheme } = useMantineColorScheme();

  // SFU-6: prompt long-lived tabs when a newer core-frontend build is deployed.
  useVersionNotification({
    app: 'core-frontend',
    currentBuildId: import.meta.env.VITE_BUILD_ID,
    onUpdateAvailable: () =>
      notifications.show({
        id: 'frontend-version-update', // stable id → no duplicate toast
        title: t('version_update.title'),
        message: (
          <Stack gap={6}>
            <Text size="sm">{t('version_update.message')}</Text>
            <Button
              size="xs"
              variant="light"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => window.location.reload()}
            >
              {t('version_update.action')}
            </Button>
          </Stack>
        ),
        autoClose: false,
        color: 'blue',
      }),
  });

  // OSF-9: Sync language from user profile on auth (fixes browser-language fallback)
  useEffect(() => {
    if (user?.languageCode && user.languageCode !== i18n.language) {
      i18n.changeLanguage(user.languageCode);
      document.documentElement.lang = user.languageCode;
    }
  }, [user?.languageCode]);

  // Synchronize dayjs locale with i18n language
  useEffect(() => {
    const locale = i18n.language;
    dayjs.locale(locale);
  }, [i18n.language]);

  // UPROF-1.7: silent profile refresh (GET /me → update query cache + auth store).
  // Replaces the 1.2 fetchProfile() in cross-tab sync paths, which flipped the auth
  // store isLoading and made AuthGuard remount the whole page on every sync.
  const syncAuthUser = useProfileSync();

  // Cross-tab preferences sync via WebSocket (HDR-5)
  usePreferencesSync({
    userGuid: user?.userGuid ?? null,
    onLanguageChange: (code: string) => {
      console.log('[App] Language sync received:', code);
      i18n.changeLanguage(code);
      document.documentElement.lang = code;
    },
    onCurrencyChange: (code: string) => {
      console.log('[App] Currency sync received:', code);
      // Refresh user to update preferred_currency in auth state (silent — no remount)
      void syncAuthUser();
    },
    onThemeChange: (theme: 'light' | 'dark' | 'auto') => {
      console.log('[App] Theme sync received:', theme);
      setColorScheme(theme);
    },
    enabled: isAuthenticated,
  });

  // Cross-tab profile sync via WebSocket (UPROF-1.4): a PROFILE_UPDATED ping in
  // another tab/device refreshes this tab's auth store + query cache from GET /me,
  // so the header's display-name/avatar stay consistent. display_name is never
  // recomputed here (DD-2).
  useProfileRealtimeSync({
    userGuid: user?.userGuid ?? null,
    onProfileChanged: syncAuthUser,
    enabled: isAuthenticated,
  });

  // Global favorites sync via WebSocket - enables cross-app sync on ALL pages
  // (not just /favorites page) - fixes favorites-sync-2 bug
  useFavoritesSync();

  // Global messages sync via WebSocket — unread badge + notifications on all pages
  useMessagesSync();

  // Global orders sync via WebSocket — order history updates on status changes
  useOrdersSync();

  // MPRN-6: global "store is ready" toast via WebSocket — reaches the owner even
  // when no creation wizard is open (waitlist activation / admin approval), cross-tab
  // and cross-device.
  useMerchantReadySync();

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <RouteAnnouncer />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/email-verified" element={<EmailVerifiedPage />} />
        {/* OAuth redirect landing (TGOIDC-5). Public: the user arrives here logged out. */}
        <Route path="/auth/callback/:provider" element={<OAuthCallbackPage />} />
        <Route path="/logged-out" element={<LoggedOutPage />} />
        {/* Portal routes for unified cross-domain authentication (Story 8.2) */}
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/register" element={<PortalRegisterPage />} />
              <Route
                path="/"
                element={
                  <AppShell>
                    <HomePage />
                  </AppShell>
                }
              />
              <Route
                path="/search"
                element={
                  <AppShell>
                    <SearchPage />
                  </AppShell>
                }
              />
              <Route
                path="/merchants"
                element={
                  <AppShell>
                    <MerchantsPage />
                  </AppShell>
                }
              />
              <Route
                path="/news"
                element={
                  <AppShell>
                    <NewsRoadmapPage />
                  </AppShell>
                }
              />

              {/* Protected routes */}
              <Route
                path="/profile/*"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <ProfilePage />
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/orders"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <OrdersPage />
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/finances"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <FinancesPage />
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/favorites"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <FavoritesPage />
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/carts"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <CartsPage />
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/messages/:conversationId?"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <MessagesPage />
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/create-merchant"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <CreateMerchantPage />
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/my-merchants"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <MyMerchantsPage />
                    </AppShell>
                  </AuthGuard>
                }
              />

              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <AdminLayout>
                        <DashboardPage />
                      </AdminLayout>
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <AdminLayout>
                        <RolesPage />
                      </AdminLayout>
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <AdminLayout>
                        <UsersPage />
                      </AdminLayout>
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/merchants"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <AdminLayout>
                        <AdminMerchantsPage />
                      </AdminLayout>
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/permissions"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <AdminLayout>
                        <PermissionsPage />
                      </AdminLayout>
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <AdminLayout>
                        <SettingsPage />
                      </AdminLayout>
                    </AppShell>
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/news"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <AdminLayout>
                        <NewsPage />
                      </AdminLayout>
                    </AppShell>
                  </AuthGuard>
                }
              />

              <Route
                path="/admin/catalog/*"
                element={
                  <AuthGuard redirectTo="/login" loadingComponent={<PageLoadingFallback />}>
                    <AppShell>
                      <AdminLayout>
                        <CatalogAdminPage />
                      </AdminLayout>
                    </AppShell>
                  </AuthGuard>
                }
              />

              {/* Test pages (development only) */}
              <Route
                path="/test-api"
                element={
                  <AppShell>
                    <TestAPIPage />
                  </AppShell>
                }
              />

              {/* Legal pages */}
              <Route
                path="/privacy"
                element={
                  <AppShell>
                    <PrivacyPage />
                  </AppShell>
                }
              />
              <Route
                path="/terms"
                element={
                  <AppShell>
                    <TermsPage />
                  </AppShell>
                }
              />
              <Route
                path="/deleteme"
                element={
                  <AppShell>
                    <DeleteMePage />
                  </AppShell>
                }
              />

              {/* Error pages */}
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="/forbidden" element={<ForbiddenPage />} />
              <Route path="/server-error" element={<ServerErrorPage />} />
              <Route path="/service-unavailable" element={<ServiceUnavailablePage />} />

              {/* 404 - Must be last */}
              <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    // ThemeProvider wraps MantineProvider and selects baseline | editorial
    // bundle from localStorage / ?tt-theme query param. Default = baseline.
    // Editorial CSS chunks (incl. variable fonts) load lazily on activation.
    <ThemeProvider>
      <ModalsProvider>
      <ErrorBoundary>
        <PlatformBootstrapProvider>
          <QueryClientProvider client={queryClient}>
            <DatesProvider settings={{ locale: dayjs.locale() }}>
              <AuthProvider
                apiBaseUrl={AUTH_API_URL}
              onSessionExpired={() => {
                console.log('[Auth] Session expired');
              }}
            >
              <UserPreferencesProvider>
                <CurrencyProvider>
                  <WebSocketWrapper>
                    <Notifications {...NOTIFICATIONS_CONFIG} />
                    {/* Auto-logout warning (SLC-1.8): the server decides, this only shows the minute before */}
                    <IdleSessionWarning />
                    <BrowserRouter>
                      <MultiCartProvider>
                        <AppContent />
                        <MultiCartDrawerWrapper />
                      </MultiCartProvider>
                    </BrowserRouter>
                  </WebSocketWrapper>
                </CurrencyProvider>
              </UserPreferencesProvider>
              </AuthProvider>
            </DatesProvider>
          </QueryClientProvider>
        </PlatformBootstrapProvider>
      </ErrorBoundary>
      </ModalsProvider>
    </ThemeProvider>
  );
}

// OAuth config now provided by PlatformBootstrapProvider (useOAuthConfig hook)